import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { NotificationView } from './types';

/** Minimum time an in-progress phase stays visible, so fast ops don't flash past. */
const MIN_IN_PROGRESS_MS = 1_800;

interface NotificationActivity extends NotificationView {
  seq: number;
}

interface State {
  items: Record<string, NotificationActivity>;
  seq: number;
}

type Event = { type: 'upsert'; activity: NotificationView } | { type: 'remove'; id: string };

function reducer(state: State, event: Event): State {
  switch (event.type) {
    case 'remove': {
      if (!(event.id in state.items)) return state;
      const items = { ...state.items };
      delete items[event.id];
      return { ...state, items };
    }
    case 'upsert': {
      const existing = state.items[event.activity.id];
      // Keep the original seq on updates so progress ticks don't reshuffle the deck.
      const seq = existing ? existing.seq : state.seq + 1;
      return {
        seq: existing ? state.seq : seq,
        items: { ...state.items, [event.activity.id]: { ...event.activity, seq } }
      };
    }
  }
}

/** Ordered most-recent-first (stable across progress updates). */
function select(state: State): NotificationView[] {
  return Object.values(state.items).sort((a, b) => b.seq - a.seq);
}

export interface UseNotificationsState {
  activities: NotificationView[];
  /** Seed once from the snapshot query (idempotent — repeated calls are ignored). */
  seed: (list: readonly NotificationView[]) => void;
  /** Merge one live delta (subscription). */
  ingest: (notification: NotificationView) => void;
  /** Apply a fresh snapshot after missed deltas: in-progress items it lacks are dropped. */
  resync: (list: readonly NotificationView[]) => void;
}

/**
 * Notification-center state engine (transport-agnostic): dedup by id, stable order, a readable
 * minimum for in-progress phases, and per-notification `ttlMs` removal once finished. The host
 * feeds it query results via `seed` (later re-reads via `resync`) and subscription deltas via
 * `ingest`. An item is "in progress" iff it carries a `progress` value; everything else is final.
 */
export function useNotificationsState(): UseNotificationsState {
  const [state, dispatch] = useReducer(reducer, { items: {}, seq: 0 });
  // One pending timer per id — either a deferred final state or a removal.
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const inProgressSince = useRef<Map<string, number>>(new Map());
  const seeded = useRef(false);

  const ingest = useCallback((notification: NotificationView) => {
    const t = timers.current;
    const since = inProgressSince.current;

    const pending = t.get(notification.id);
    if (pending) {
      clearTimeout(pending);
      t.delete(notification.id);
    }

    const isFinal = notification.progress == null;
    if (!isFinal) {
      if (!since.has(notification.id)) since.set(notification.id, performance.now());
      dispatch({ type: 'upsert', activity: notification });
      return;
    }

    // Final: keep any in-progress phase readable for a minimum, then flip + schedule removal.
    const started = since.get(notification.id);
    const shownFor = started === undefined ? Number.POSITIVE_INFINITY : performance.now() - started;
    const holdFor = MIN_IN_PROGRESS_MS - shownFor;

    const commitFinal = () => {
      since.delete(notification.id);
      dispatch({ type: 'upsert', activity: notification });
      const removal = setTimeout(() => {
        t.delete(notification.id);
        dispatch({ type: 'remove', id: notification.id });
      }, notification.ttlMs);
      t.set(notification.id, removal);
    };

    if (holdFor > 0) {
      const hold = setTimeout(() => {
        t.delete(notification.id);
        commitFinal();
      }, holdFor);
      t.set(notification.id, hold);
    } else {
      commitFinal();
    }
  }, []);

  const seed = useCallback(
    (list: readonly NotificationView[]) => {
      if (seeded.current) return;
      seeded.current = true;
      list.forEach(ingest);
    },
    [ingest]
  );

  const resync = useCallback(
    (list: readonly NotificationView[]) => {
      seeded.current = true;
      const current = new Set(list.map(notification => notification.id));
      // An id with a pending timer already has its final state on the way.
      for (const id of [...inProgressSince.current.keys()]) {
        if (current.has(id) || timers.current.has(id)) continue;
        inProgressSince.current.delete(id);
        dispatch({ type: 'remove', id });
      }
      for (const notification of list) {
        // A final delta beat the snapshot; its stale progress must not revive the bar.
        if (notification.progress != null && timers.current.has(notification.id)) continue;
        ingest(notification);
      }
    },
    [ingest]
  );

  useEffect(() => {
    const t = timers.current;
    return () => {
      t.forEach(clearTimeout);
      t.clear();
    };
  }, []);

  const activities = useMemo(() => select(state), [state]);
  return { activities, seed, ingest, resync };
}
